import { faDownload } from "@fortawesome/pro-light-svg-icons";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { VaporIcon } from "@vapor/react-icons";
import { Box, Stack, useTheme } from "@vapor/react-material";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDownloadTaxSubmissionOutcomeMutation } from "../../../../../../core/api/use-download-tax-submission-outcome/useDownloadTaxSubmissionOutcomeMutation";
import { useDownloadTaxSubmissionPdfOutcomeMutation } from "../../../../../../core/api/use-download-tax-submission-pdf-outcome/useDownloadTaxSubmissionPdfOutcomeMutation";
import type { TaxSubmissionDetailDTO } from "../../../../../../core/api/use-get-tax-submission-detail/TaxSubmissionDetailDTO";
import { GovernmentAgencyType } from "../../../../../../core/api/use-get-tax-submission-detail/TaxSubmissionDetailDTO";
import { downloadFile } from "../../../../../../core/utils/downloadFile";
import {
    getSummaryTranslation,
    groupOutcomeTypes,
} from "../../../../../utils/outcomesTypes";
import type { SecondaryOption } from "../TaxSubmissionDetailToolbar";

export const useGetCtaDownloadOutcomes = (
    taxSubmissionDetail: TaxSubmissionDetailDTO,
): SecondaryOption[] => {
    const { t } = useTranslation();

    const theme = useTheme();
    const downloadPdfOutcomeMutation =
        useDownloadTaxSubmissionPdfOutcomeMutation();
    const downloadOutcomeMutation = useDownloadTaxSubmissionOutcomeMutation();

    const onClickDownloadPdfOutcome = useCallback(
        async (
            taxSubmissionId: string,
            outcomeTypes: string[],
            outcomeGroupType: string,
        ) => {
            try {
                const resourceData =
                    await downloadPdfOutcomeMutation.mutateAsync({
                        taxSubmissionId,
                        outcomeTypes,
                    });

                const filename: string = "Riepilogativo " + outcomeGroupType;
                await downloadFile(
                    resourceData.content,
                    filename,
                    resourceData.type,
                );
            } catch {
                // error notification already shown by handleHttpError
            }
        },
        [downloadPdfOutcomeMutation],
    );

    const onClickDownloadOutcome = useCallback(
        async (taxSubmissionId: string, outcomeType: string) => {
            try {
                const resourceData = await downloadOutcomeMutation.mutateAsync({
                    taxSubmissionId,
                    outcomeType,
                });

                const filename: string = "Riepilogativo " + outcomeType;
                await downloadFile(
                    resourceData.content,
                    filename,
                    resourceData.type,
                );
            } catch {
                // error notification already shown by handleHttpError
            }
        },
        [downloadOutcomeMutation],
    );

    const getOptions = useCallback(() => {
        if (
            !!taxSubmissionDetail.outcomeTypes &&
            taxSubmissionDetail.outcomeTypes.length > 0
        ) {
            if (
                taxSubmissionDetail.governmentAgency.type ===
                GovernmentAgencyType.ade
            ) {
                const groupedOutcomeTypes = groupOutcomeTypes(
                    taxSubmissionDetail.outcomeTypes,
                );
                return groupedOutcomeTypes.map((outcomeGroupType) => {
                    return {
                        icon: faDownload,
                        label: getSummaryTranslation(outcomeGroupType, t),
                        onClick: () =>
                            onClickDownloadPdfOutcome(
                                taxSubmissionDetail.id,
                                taxSubmissionDetail.outcomeTypes.filter(
                                    (outcomeType) =>
                                        outcomeType.includes(outcomeGroupType),
                                ),
                                outcomeGroupType,
                            ),
                        loading: downloadPdfOutcomeMutation.isPending,
                    };
                });
            }

            return taxSubmissionDetail.outcomeTypes.map((outcomeType) => {
                return {
                    icon: faDownload,
                    label: getSummaryTranslation(outcomeType, t),
                    onClick: () =>
                        onClickDownloadOutcome(
                            taxSubmissionDetail.id,
                            outcomeType,
                        ),
                    loading: downloadOutcomeMutation.isPending,
                };
            });
        } else {
            return [];
        }
    }, [
        downloadOutcomeMutation.isPending,
        downloadPdfOutcomeMutation.isPending,
        onClickDownloadOutcome,
        onClickDownloadPdfOutcome,
        t,
        taxSubmissionDetail.governmentAgency.type,
        taxSubmissionDetail.id,
        taxSubmissionDetail.outcomeTypes,
    ]);

    return getOptions().map((option) => ({
        element: (
            <Stack direction="row" onClick={option.onClick}>
                <VaporIcon icon={option?.icon} color="interactive" />
                <Box sx={{ mr: 1 }}></Box>
                <Typography color={theme.palette.primary.interactiveDefault}>
                    {option.label}
                </Typography>
            </Stack>
        ),
        loading: option.loading,
    }));
};
